/**
 * ProfileModal - Create/Edit Profile Modal
 * 
 * Features:
 * - Profile name and description input
 * - Permission picker grouped by category
 * - Select all / deselect all per category
 * - Validation for required fields
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    createProfile,
    updateProfile,
    closeCreateModal,
    closeEditModal,
    selectIsCreateModalOpen,
    selectIsEditModalOpen,
    selectSelectedProfile,
    selectPermissionGroups,
    selectIsLoadingPermissions,
    selectIsCreating,
    selectIsUpdating,
    selectOperationError,
} from '../../store/slices/profileSlice';

const ProfileModal = () => {
    const dispatch = useDispatch();
    
    // Selectors
    const isCreateModalOpen = useSelector(selectIsCreateModalOpen);
    const isEditModalOpen = useSelector(selectIsEditModalOpen);
    const selectedProfile = useSelector(selectSelectedProfile);
    const permissionGroups = useSelector(selectPermissionGroups);
    const isLoadingPermissions = useSelector(selectIsLoadingPermissions);
    const isCreating = useSelector(selectIsCreating);
    const isUpdating = useSelector(selectIsUpdating);
    const operationError = useSelector(selectOperationError);

    const isEditMode = isEditModalOpen && selectedProfile;
    const isSubmitting = isCreating || isUpdating;

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState(new Set());
    const [errors, setErrors] = useState({});

    // Initialize form when editing
    useEffect(() => {
        if (isEditMode && selectedProfile) {
            setName(selectedProfile.name || '');
            setDescription(selectedProfile.description || '');
            setSelectedPermissions(new Set(selectedProfile.permissions || []));
        } else {
            setName('');
            setDescription('');
            setSelectedPermissions(new Set());
        }
        setErrors({});
    }, [isEditMode, selectedProfile]);

    // Get all permission codes for "select all" functionality
    const allPermissionCodes = useMemo(() => {
        return permissionGroups.flatMap(group => 
            group.permissions.map(p => p.code)
        );
    }, [permissionGroups]);

    // Handlers
    const handleClose = () => {
        if (isEditMode) {
            dispatch(closeEditModal());
        } else {
            dispatch(closeCreateModal());
        }
    };

    const handlePermissionToggle = (code) => {
        setSelectedPermissions(prev => {
            const next = new Set(prev);
            if (next.has(code)) {
                next.delete(code);
            } else {
                next.add(code);
            }
            return next;
        });
    };

    const handleCategoryToggle = (group) => {
        const categoryCodes = group.permissions.map(p => p.code);
        const allSelected = categoryCodes.every(code => selectedPermissions.has(code));
        
        setSelectedPermissions(prev => {
            const next = new Set(prev);
            if (allSelected) {
                categoryCodes.forEach(code => next.delete(code));
            } else {
                categoryCodes.forEach(code => next.add(code));
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        setSelectedPermissions(new Set(allPermissionCodes));
    };

    const handleDeselectAll = () => {
        setSelectedPermissions(new Set());
    };

    const validate = () => {
        const newErrors = {};
        
        if (!name.trim()) {
            newErrors.name = 'Profile name is required';
        } else if (name.trim().length < 2) {
            newErrors.name = 'Profile name must be at least 2 characters';
        } else if (name.trim().length > 100) {
            newErrors.name = 'Profile name cannot exceed 100 characters';
        }

        if (description && description.length > 500) {
            newErrors.description = 'Description cannot exceed 500 characters';
        }

        if (selectedPermissions.size === 0) {
            newErrors.permissions = 'At least one permission is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validate()) return;

        const profileData = {
            name: name.trim(),
            description: description.trim() || null,
            permissions: Array.from(selectedPermissions),
        };

        if (isEditMode) {
            dispatch(updateProfile({ 
                uuid: selectedProfile.uuid, 
                profileData 
            }));
        } else {
            dispatch(createProfile(profileData));
        }
    };

    // Check if category has all permissions selected
    const isCategoryFullySelected = (group) => {
        return group.permissions.every(p => selectedPermissions.has(p.code));
    };

    const isCategoryPartiallySelected = (group) => {
        const selected = group.permissions.filter(p => selectedPermissions.has(p.code));
        return selected.length > 0 && selected.length < group.permissions.length;
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-black/50 transition-opacity"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                        <h2 className="text-lg font-display font-bold text-gray-900">
                            {isEditMode ? 'Edit Profile' : 'Create Profile'}
                        </h2>
                        <button
                            onClick={handleClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
                            {/* Error Display */}
                            {operationError && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                                    {operationError}
                                </div>
                            )}

                            {/* System Profile Warning */}
                            {isEditMode && selectedProfile?.isSystem && (
                                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm flex items-center gap-2">
                                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <span>This is a system profile. The name cannot be changed.</span>
                                </div>
                            )}

                            {/* Name Input */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Profile Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    disabled={isEditMode && selectedProfile?.isSystem}
                                    placeholder="e.g., Mechanic, Receptionist, Manager"
                                    className={`w-full px-4 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                                        errors.name ? 'border-red-300' : 'border-gray-300'
                                    } ${isEditMode && selectedProfile?.isSystem ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                )}
                            </div>

                            {/* Description Input */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Brief description of this profile's purpose..."
                                    rows={2}
                                    className={`w-full px-4 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none ${
                                        errors.description ? 'border-red-300' : 'border-gray-300'
                                    }`}
                                />
                                {errors.description && (
                                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                                )}
                            </div>

                            {/* Permissions Section */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Permissions <span className="text-red-500">*</span>
                                        <span className="ml-2 text-gray-400 font-normal">
                                            ({selectedPermissions.size} selected)
                                        </span>
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={handleSelectAll}
                                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                                        >
                                            Select All
                                        </button>
                                        <span className="text-gray-300">|</span>
                                        <button
                                            type="button"
                                            onClick={handleDeselectAll}
                                            className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                </div>

                                {errors.permissions && (
                                    <p className="mb-3 text-sm text-red-600">{errors.permissions}</p>
                                )}

                                {isLoadingPermissions ? (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-emerald-600"></div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {permissionGroups.map((group) => (
                                            <div 
                                                key={group.category} 
                                                className="border border-gray-200 rounded-xl overflow-hidden"
                                            >
                                                {/* Category Header */}
                                                <button
                                                    type="button"
                                                    onClick={() => handleCategoryToggle(group)}
                                                    className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                                                            isCategoryFullySelected(group)
                                                                ? 'bg-emerald-600 border-emerald-600'
                                                                : isCategoryPartiallySelected(group)
                                                                    ? 'bg-emerald-100 border-emerald-600'
                                                                    : 'border-gray-300'
                                                        }`}>
                                                            {isCategoryFullySelected(group) && (
                                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            )}
                                                            {isCategoryPartiallySelected(group) && (
                                                                <div className="w-2 h-2 bg-emerald-600 rounded-sm"></div>
                                                            )}
                                                        </div>
                                                        <span className="font-semibold text-gray-900">
                                                            {group.displayName}
                                                        </span>
                                                        <span className="text-xs text-gray-400">
                                                            ({group.permissions.filter(p => selectedPermissions.has(p.code)).length}/{group.permissions.length})
                                                        </span>
                                                    </div>
                                                </button>

                                                {/* Permissions Grid */}
                                                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                    {group.permissions.map((permission) => (
                                                        <label
                                                            key={permission.code}
                                                            className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedPermissions.has(permission.code)}
                                                                onChange={() => handlePermissionToggle(permission.code)}
                                                                className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                                            />
                                                            <div>
                                                                <span className="text-sm font-medium text-gray-700">
                                                                    {permission.name}
                                                                </span>
                                                                {permission.description && (
                                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                                        {permission.description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={isSubmitting}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {isSubmitting && (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                                )}
                                {isEditMode ? 'Save Changes' : 'Create Profile'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ProfileModal;
